import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiClientError } from '@/lib/api-client';
import { useBatchContext } from '@/app/BatchContext';
import { fetchDashboard } from './api';
import type { DashboardDto } from './types';

const KEY = 'dashboard' as const;

/**
 * Dashboard query for the currently selected batch.
 *
 * P1-#7: when the backend signals the batch is gone (404) we proactively
 * clear `selectedBatchId` so the 30-second poll stops hammering a deleted
 * batch and the UI rerenders with the no-batch hint. 403 is surfaced as
 * `isForbidden` so the consumer can show a "select another batch" path
 * instead of a generic empty state.
 */
export const useDashboard = (batchId: string | undefined) => {
  const { setBatchId } = useBatchContext();
  const query = useQuery<DashboardDto, ApiClientError>({
    queryKey: [KEY, batchId],
    queryFn: () => fetchDashboard(batchId!),
    enabled: !!batchId,
    refetchInterval: 30_000,
    retry: (failureCount, error) => {
      // Do not retry on auth/permission/not-found terminal errors.
      if (
        error instanceof ApiClientError &&
        (error.status === 401 || error.status === 403 || error.status === 404)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Clear the stale selection so subsequent polls stop firing against a
  // batch that no longer exists. Forbidden is *not* cleared — the user may
  // be on a tab they just lost permission to; clearing would mask the
  // missing-permission UI.
  useEffect(() => {
    if (query.error instanceof ApiClientError && query.error.status === 404) {
      setBatchId(null);
    }
  }, [query.error, setBatchId]);

  const isNotFound = query.error instanceof ApiClientError && query.error.status === 404;
  const isForbidden = query.error instanceof ApiClientError && query.error.status === 403;

  return { ...query, isNotFound, isForbidden };
};
